/**
 * A note arriving at the caret: writing it, and adding one to the chord just written.
 *
 * Two things put a note here -- a letter typed on a computer keyboard and a key played on a
 * MIDI one -- and they differ only in how the pitch is arrived at. Once it has been arrived
 * at the rest is the same: the note is written over what the caret stands on, it may be tied
 * back into the note behind it, and it may join the note just written instead of following
 * it. So the pitch is settled first, as a {@link Sounded}, and both ways in share everything
 * after that.
 *
 * The letters travel beside the pitch because they are not always in it. They are the
 * letters on the screen, and what is stored may be neither of them: a letter typed into a
 * concert score is stored in the part's own key, and one typed on a drum staff is stored as
 * whichever drum is written nearest it. The next bare letter is spelled near the one the
 * user last saw, so it is that one that has to be remembered.
 */

import { note as makeNote } from "../document/create";
import { locateEvent } from "../document/query";
import type { NotePitch, Step } from "../document/pitch";
import { positionFor } from "../document/tab";
import type { Score } from "../document/types";
import { octaveFor, staffAt, typedPitch } from "./entry-spell";
import type { Typed } from "./entry-spell";
import { refuse, tieBack, write } from "./entry-write";
import type { EntryResult, EntryState } from "./entry-write";

/** A note settled on: what will be stored, and the letters it was read as on the screen. */
export interface Sounded {
  readonly pitch: NotePitch;
  readonly step: Step;
  readonly octave: number;
}

/** A typed letter as the note it means on the staff the caret is on. */
export function typedSound(
  score: Score,
  state: EntryState,
  typed: Typed,
  tieStop: boolean,
): Sounded {
  const octave = typed.octave ?? octaveFor(state.step, state.octave, typed.step);
  const staff = staffAt(
    score,
    state.caret?.partId ?? null,
    state.caret?.measureId ?? null,
    state.concert,
  );
  return { pitch: typedPitch(typed, octave, tieStop, staff), step: typed.step, octave };
}

/** Write the note at the caret and step past it. */
export function writeSound(score: Score, state: EntryState, sound: Sounded): EntryResult {
  const event = makeNote([sound.pitch], state.duration);
  const result = write(score, state, event);
  if (result.refusal !== null) return result;

  // The tie is matched against the note as it is stored, which is not always the note that
  // was played: a letter on a drum staff lands on the drum nearest it, and a letter typed
  // into a concert score is stored in the part's own key.
  const back =
    state.tie && state.last !== null
      ? tieBack(score, state.last, sound.pitch.step, sound.pitch.octave)
      : null;
  return {
    ...result,
    commands: back === null ? result.commands : [...result.commands, back],
    state: { ...result.state, octave: sound.octave, step: sound.step },
  };
}

/** Add the note to the chord just written, rather than writing it after it. */
export function chordSound(score: Score, state: EntryState, sound: Sounded): EntryResult {
  if (state.last === null) return refuse(state, "there is no note to add to yet");
  const at = locateEvent(score, state.last);
  if (at === null) return refuse(state, "the note to add to is no longer there");
  if (at.event.kind !== "note") return refuse(state, "a rest cannot be made into a chord");

  const { pitch } = sound;
  const already = at.event.pitches.some(
    (head) =>
      head.step === pitch.step && head.octave === pitch.octave && head.alter === pitch.alter,
  );
  if (already) return refuse(state, "that note is already in the chord");

  const tuning = staffAt(
    score,
    state.caret?.partId ?? null,
    state.caret?.measureId ?? null,
    state.concert,
  ).tuning;
  // A chord on a fretted instrument is played across strings: a head added to one keeps off
  // the strings its other heads are already on, however low the fret there would be.
  const taken = new Set(
    at.event.pitches.map((head) => head.tab?.string).filter((string) => string !== undefined),
  );
  const added = tuning === null ? pitch : { ...pitch, tab: positionFor(tuning, pitch, taken) };
  return {
    state: { ...state, octave: sound.octave, step: sound.step },
    commands: [{ kind: "setPitches", eventId: at.event.id, pitches: [...at.event.pitches, added] }],
    refusal: null,
  };
}
